import { AppIcon } from '@/components/AppIcon';
import { DocumentFrame } from '@/components/DocumentFrame';
import { ErrorNote, OkNote, StatusPill, Table, lineQuantity, shortDate } from '@/components/ui';
import { formatDateTime } from '@/format';
import { useStores } from '@/hooks/inventory';
import { refsOf, type Operation } from '@/offline/outbox';
import { canFix, fixPiece, pieceStatus, sendPieceAgain, throwPieceAway } from '@/offline/pieceActions';
import type { Piece } from '@/offline/pieces';
import type { DrawLine } from '@/components/DrawLines';
import type { QueuedAdjustmentForm } from '@/screens/Adjustments';
import type { QueuedIssueForm } from '@/screens/Consumption';
import type { QueuedReceiptForm } from '@/screens/Inward';
import type { QueuedTransferForm } from '@/screens/Transfers';
import { screenLabel } from '@/shell/modules';
import { useConnection } from '@/stores/connection';
import { useShellNav } from '@/stores/shellNav';

/**
 * One piece of work saved on this PC, on its own screen (.docs/023): what it
 * was, as it was typed; where it has got to; and, when the server refused it,
 * what can be done about that. Opened from Offline work, and from a "Not sent
 * yet" row on the list it was typed on.
 */

/** Screens that open one of their documents on arrival, given its id. */
const OPENS_DOCUMENTS = new Set(['inv-inward', 'inv-consume', 'inv-xfer', 'inv-adjust']);

export function SavedWorkDetail({
  piece,
  all,
  backLabel,
  onBack,
  onFix,
  onOpenDocument,
  onDone,
}: {
  piece: Piece;
  /** Everything saved on this PC, for what this piece waits on. */
  all: readonly Operation[];
  backLabel: string;
  onBack: () => void;
  /** Put it right here rather than on the screen it was typed on. */
  onFix?: (op: Operation) => void;
  /** Open its document here rather than going to its screen. */
  onOpenDocument?: (id: string) => void;
  /** After Send again or Throw it away, with what to say. */
  onDone: (said: string) => void;
}) {
  const online = useConnection((s) => s.online);
  const { lead, refused } = piece;
  const resultId = lead.resultId;
  const canOpen = piece.state === 'sent' && resultId !== null && (onOpenDocument !== undefined || OPENS_DOCUMENTS.has(lead.screen));

  const actions = (
    <>
      {canOpen && (
        <button
          type="button"
          className="btn btn--primary btn--withicon"
          onClick={() => (onOpenDocument ? onOpenDocument(resultId) : useShellNav.getState().navigate(lead.screen, { recordId: resultId }))}
        >
          <AppIcon kind="details" size={15} />
          Open {piece.documentNo ?? 'the document'}
        </button>
      )}
      {refused && (
        <>
          <button
            type="button"
            className="btn btn--danger"
            onClick={() => void throwPieceAway(piece).then((gone) => gone && onDone(`${lead.describes}: thrown away.`))}
          >
            Throw it away
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => void sendPieceAgain(piece).then(() => onDone(`${lead.describes}: sent again.`))}
          >
            Send it again
          </button>
          {canFix(piece) && (
            <button
              type="button"
              className="btn btn--primary btn--withicon"
              onClick={() => (onFix ? onFix(refused) : fixPiece(piece))}
            >
              <AppIcon kind="edit" size={15} />
              Fix it
            </button>
          )}
        </>
      )}
    </>
  );

  return (
    <DocumentFrame
      onBack={onBack}
      backLabel={backLabel}
      title={
        <>
          <h2>{piece.documentNo ?? lead.describes}</h2>
          <StatusPill status={pieceStatus(piece)} />
        </>
      }
      actions={actions}
    >
      {piece.state === 'waiting' && (
        <p className="docpage__note" role="note">
          {online
            ? 'Saved on this PC and being sent now. It gets its document number when the server has it.'
            : 'Saved on this PC. It will be sent by itself when the connection is back — there is nothing more to do.'}
        </p>
      )}
      {refused && (
        <>
          {/* The server's own words: written for whoever hit the problem. */}
          <ErrorNote message={refused.lastError ?? 'The server refused it.'} />
          <p className="muted">
            Put right what the server says, then send it again. Work of the same kind saved after this one waits until it is
            settled.
          </p>
        </>
      )}
      {piece.state === 'sent' && (
        <OkNote message={`Sent${piece.documentNo ? ` as ${piece.documentNo}` : ''} on ${formatDateTime(new Date(piece.sentAt ?? 0))}.`} />
      )}

      <section className="stack docpage__section">
        <h3 className="formsection">About</h3>
        <dl className="detaillist">
          <dt>What</dt>
          <dd>{lead.describes}</dd>
          <dt>Typed on</dt>
          <dd>{screenLabel(lead.screen)}</dd>
          <dt>Saved on this PC</dt>
          <dd>{formatDateTime(new Date(piece.createdAt))}</dd>
          {piece.sentAt !== null && (
            <>
              <dt>Sent</dt>
              <dd>{formatDateTime(new Date(piece.sentAt))}</dd>
            </>
          )}
          {piece.documentNo && (
            <>
              <dt>Document</dt>
              <dd className="mono">{piece.documentNo}</dd>
            </>
          )}
          {piece.state !== 'sent' && lead.attempts > 0 && (
            <>
              <dt>Tries so far</dt>
              <dd>{lead.attempts}</dd>
            </>
          )}
        </dl>
        <WaitingOn piece={piece} all={all} />
      </section>

      <SavedContents op={lead} />

      {piece.ops.length > 1 && (
        <section className="stack docpage__section">
          <h3 className="formsection">Steps</h3>
          <Table
            count={piece.ops.length}
            empty="No steps."
            head={
              <tr>
                <th>Step</th>
                <th>Status</th>
              </tr>
            }
          >
            {piece.ops.map((op) => (
              <tr key={op.id}>
                <td>{op.describes}</td>
                <td>
                  <StatusPill status={op.state === 'refused' ? 'REFUSED' : op.state === 'sent' ? 'SENT' : 'NOT_SENT'} />
                </td>
              </tr>
            ))}
          </Table>
        </section>
      )}
    </DocumentFrame>
  );
}

/**
 * What a piece of waiting work stands on, when that has not gone yet: a
 * receipt naming a supplier added on this PC goes once the supplier is made —
 * and not at all while the server refuses the supplier.
 */
export function WaitingOn({ piece, all }: { piece: Piece; all: readonly Operation[] }) {
  if (piece.state !== 'waiting') return null;
  const before = piece.ops
    .flatMap(refsOf)
    .map((ref) => all.find((o) => o.id === ref))
    .filter((o): o is Operation => o !== undefined && o.state !== 'sent' && !piece.ops.includes(o));
  const stuck = before.find((o) => o.state === 'refused');
  if (stuck) return <p className="stuck__why">Waiting on “{stuck.describes}”, which the server would not take. Put that right first.</p>;
  if (before.length) return <p className="muted">Goes after “{before[0]!.describes}”.</p>;
  return null;
}

/** The work as it was typed, for the kinds whose form is kept with it. */
function SavedContents({ op }: { op: Operation }) {
  const { data: stores } = useStores();
  const storeName = (id: string) => {
    const store = (stores ?? []).find((s) => s.id === id);
    return store ? `${store.code} — ${store.nameEn}` : '—';
  };
  if (op.form === undefined) return null;

  // A form saved by an older version may not have the shape this one expects:
  // shown only when it does, rather than taking the page down.
  if (op.kind === 'inward.create' && Array.isArray((op.form as Partial<QueuedReceiptForm>).lines)) {
    const form = op.form as QueuedReceiptForm;
    const lines = form.lines.filter((l) => l.skuId);
    return (
      <section className="stack docpage__section">
        <h3 className="formsection">Items received · {shortDate(form.deliveryDate)}</h3>
        <Table
          count={lines.length}
          empty="No items."
          head={
            <tr>
              <th>Item</th>
              <th className="num">Quantity</th>
              <th>Supplier</th>
              <th>Store</th>
              <th>Lot</th>
              <th>Invoice</th>
            </tr>
          }
        >
          {lines.map((l) => (
            <tr key={l.key}>
              <td>{l.skuLabel}</td>
              <td className="num">{lineQuantity(l.qty)}</td>
              <td>{l.supplierLabel || '—'}</td>
              <td className="dim">{storeName(l.storeId)}</td>
              <td className="dim">{l.batchNo || '—'}</td>
              <td className="dim mono">{l.invoiceRef || '—'}</td>
            </tr>
          ))}
        </Table>
      </section>
    );
  }

  const issue = op.form as Partial<QueuedIssueForm>;
  if (op.kind === 'consumption.create' && issue.header && issue.line) {
    const { header, line } = issue as QueuedIssueForm;
    return (
      <section className="stack docpage__section">
        <h3 className="formsection">Item issued</h3>
        <dl className="detaillist">
          <dt>Item</dt>
          <dd>{line.skuLabel}</dd>
          <dt>Quantity</dt>
          <dd>{lineQuantity(line.qty)}</dd>
          <dt>From store</dt>
          <dd>{storeName(header.storeId)}</dd>
          <dt>Date</dt>
          <dd>{shortDate(header.consumedOn)}</dd>
          <dt>Issued to</dt>
          <dd>{header.issuedTo || '—'}</dd>
          <dt>Remarks</dt>
          <dd>{header.remarks || '—'}</dd>
        </dl>
      </section>
    );
  }

  const moved = op.form as Partial<QueuedTransferForm>;
  if (op.kind === 'transfer.create' && moved.header && Array.isArray(moved.lines)) {
    const { header, lines } = op.form as QueuedTransferForm;
    return (
      <section className="stack docpage__section">
        <h3 className="formsection">{header.kind === 'return' ? 'Return' : 'Transfer'} · {shortDate(header.transferDate)}</h3>
        <dl className="detaillist">
          <dt>From</dt>
          <dd>{storeName(header.fromStoreId)}</dd>
          <dt>To</dt>
          <dd>{storeName(header.toStoreId)}</dd>
          <dt>Vehicle</dt>
          <dd>{header.vehicleNo || '—'}</dd>
          <dt>Remarks</dt>
          <dd>{header.remarks || '—'}</dd>
        </dl>
        <DrawnLines lines={lines} />
      </section>
    );
  }

  const adjusted = op.form as Partial<QueuedAdjustmentForm>;
  if (op.kind === 'adjustment.create' && adjusted.header && Array.isArray(adjusted.lines)) {
    const { header, lines } = op.form as QueuedAdjustmentForm;
    return (
      <section className="stack docpage__section">
        <h3 className="formsection">Adjustment · {shortDate(header.adjustmentDate)}</h3>
        <dl className="detaillist">
          <dt>Store</dt>
          <dd>{storeName(header.storeId)}</dd>
          <dt>Direction</dt>
          <dd>{header.direction === '-1' ? 'Write off (reduce stock)' : 'Write on (increase stock)'}</dd>
          <dt>Reason</dt>
          <dd>{header.reason || '—'}</dd>
        </dl>
        <DrawnLines lines={lines} />
      </section>
    );
  }

  return null;
}

/** The items of a transfer or adjustment as typed: the ones actually filled in. */
function DrawnLines({ lines }: { lines: readonly DrawLine[] }) {
  const started = lines.filter((l) => l.skuId);
  return (
    <Table
      count={started.length}
      empty="No items."
      head={
        <tr>
          <th>Item</th>
          <th className="num">Quantity</th>
        </tr>
      }
    >
      {started.map((l) => (
        <tr key={l.key}>
          <td>{l.skuLabel}</td>
          <td className="num">{lineQuantity(l.qty)}</td>
        </tr>
      ))}
    </Table>
  );
}
